// Costanti per i tipi di partita
export const TIPI_PARTITA = ["girone", "girone 2", "dirette", "ottavi", "quarti", "semifinale", "finale", "finalina"];

// Costanti per le fasce di partita
export const FASCE_PARTITA = ["1", "2", "3"];

// Formattazione della data per la visualizzazione italiana
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('it-IT');
};

// Inizializza le statistiche di base per un giocatore
export const initializePlayerStats = (giocatore) => ({
  ...giocatore,
  mete: 0,
  pieni: 0,
  vuoti1: 0,
  vuoti2: 0,
  vuoti3: 0,
  vuoti: 0,
  assist: 0,
  giallo: false,
  rosso: false,
  nero: false
});

// Trasforma i dati delle squadre includendo le statistiche iniziali
export const transformSquadreData = (squadreData) => {
  return squadreData.map(squadra => ({
    ...squadra,
    giocatori: squadra.giocatori ? squadra.giocatori.map(initializePlayerStats) : []
  }));
};

// Valida se la configurazione della partita è completa
export const isMatchConfigComplete = (squadra1, squadra2, tipoPartita, fasciaPartita) => {
  return squadra1 && squadra2 && tipoPartita && fasciaPartita;
};

// Il voto di fair play non assegnato viaggia nullo, non come zero: zero e' un giudizio vero e
// entrerebbe nella media di squadra. La casella vuota della griglia arriva qui come stringa vuota.
const votoFairPlay = (voto) =>
  (voto === '' || voto === null || voto === undefined ? null : Number(voto));

// Crea i dati della partita nel formato identico alle statistiche in ingresso
export const createMatchData = (
  isEditMode,
  partitaCorrente,
  edizioneSelezionata,
  squadra1,
  squadra2,
  giocatoriSquadra1,
  giocatoriSquadra2,
  falli1,
  falli2,
  fairPlay1,
  fairPlay2,
  tipoPartita,
  fasciaPartita,
  nome
) => {
  // Crea l'array di statistiche nel formato identico a quello in ingresso
  const statistiche = [
    ...giocatoriSquadra1.map(g => ({
      idP: g.id, // ID dell'iscrizione (Player)
      idG: g.idG || g.id,
      idS: squadra1.id, // ID della squadra
      mete: g.mete || 0,
      pieni: g.pieni || 0,
      vuoti1: g.vuoti1 || 0,
      vuoti2: g.vuoti2 || 0,
      vuoti3: g.vuoti3 || 0,
      assist: g.assist || 0,
      giallo: g.giallo || false,
      rosso: g.rosso || false,
      nero: g.nero || false
    })),
    ...giocatoriSquadra2.map(g => ({
      idP: g.id, // ID dell'iscrizione (Player)
      idG: g.idG || g.id,
      idS: squadra2.id, // ID della squadra
      mete: g.mete || 0,
      pieni: g.pieni || 0,
      vuoti1: g.vuoti1 || 0,
      vuoti2: g.vuoti2 || 0,
      vuoti3: g.vuoti3 || 0,
      assist: g.assist || 0,
      giallo: g.giallo || false,
      rosso: g.rosso || false,
      nero: g.nero || false
    }))
  ];
  return {
    id: isEditMode ? partitaCorrente?.id : null,
    squadra1: squadra1.id,
    squadra2: squadra2.id,
    tipo: tipoPartita,
    fascia: fasciaPartita,
    torneo: edizioneSelezionata?.id || edizioneSelezionata,
    falli1: falli1 || 0,
    falli2: falli2 || 0,
    fairPlay1: votoFairPlay(fairPlay1),
    fairPlay2: votoFairPlay(fairPlay2),
    nome: nome || null,
    statistiche: statistiche
  };
};

// Fonde le statistiche arrivate dal server nella distinta di una squadra. Il server manda solo
// le righe che esistono, quindi si parte dalla distinta azzerata e si sovrascrive chi ha segnato.
// L'aggancio e' su idP, l'id della riga di iscrizione: le statistiche sono salvate sul giocatore
// anagrafico, ma la griglia ragiona per player.
export const applicaStatistiche = (giocatori, statistiche) => {
  const perPlayer = new Map((statistiche || []).map(s => [s.idP, s]));

  return (giocatori || []).map(giocatore => {
    const base = initializePlayerStats(giocatore);
    const stat = perPlayer.get(giocatore.id);

    if (!stat) {
      return base;
    }

    return {
      ...base,
      idG: stat.idG ?? base.idG,
      mete: stat.mete || 0,
      pieni: stat.pieni || 0,
      vuoti1: stat.vuoti1 || 0,
      vuoti2: stat.vuoti2 || 0,
      vuoti3: stat.vuoti3 || 0,
      vuoti: (stat.vuoti1 || 0) + (stat.vuoti2 || 0) + (stat.vuoti3 || 0),
      assist: stat.assist || 0,
      giallo: stat.giallo || false,
      rosso: stat.rosso || false,
      nero: stat.nero || false
    };
  });
};

// Calcola il totale di una statistica per una squadra
export const calculateTeamTotal = (giocatori, stat) => {
  return giocatori.reduce((sum, g) => sum + (g[stat] || 0), 0);
};

// Determina il risultato della partita per una squadra
export const getMatchResult = (giocatoriSquadra1, giocatoriSquadra2, falli1, falli2, tipoPartita, teamNumber) => {
  if (!giocatoriSquadra1 || !giocatoriSquadra2) return null;
  
  // Calcola le mete base per ogni squadra
  const meteBase1 = calculateTeamTotal(giocatoriSquadra1, 'mete');
  const meteBase2 = calculateTeamTotal(giocatoriSquadra2, 'mete');
  
  // Aggiunge le mete dai falli (ogni 3 falli = 1 meta per gli avversari)
  const meteConFalli1 = meteBase1 + Math.floor((falli2 || 0) / 3);
  const meteConFalli2 = meteBase2 + Math.floor((falli1 || 0) / 3);
  
  // Se non ci sono statistiche significative, non mostra risultato
  if (meteConFalli1 === 0 && meteConFalli2 === 0 && (falli1 || 0) === 0 && (falli2 || 0) === 0) {
    return null;
  }
  
  let result;
  
  // Determina il vincitore in base alle mete totali
  if (meteConFalli1 > meteConFalli2) {
    result = teamNumber === 1 ? 'win' : 'loss';
  } else if (meteConFalli1 < meteConFalli2) {
    result = teamNumber === 1 ? 'loss' : 'win';
  } else {
    // In caso di parità di mete
    if (tipoPartita === 'girone' || tipoPartita === 'girone 2') {
      result = 'draw';
    } else {
      // Per partite diverse da girone, applica i criteri di spareggio
      result = getTiebreakResult(giocatoriSquadra1, giocatoriSquadra2, falli1, falli2, teamNumber);
    }
  }
  
  return result;
};

// Gestisce gli spareggi per partite diverse da girone
const getTiebreakResult = (giocatoriSquadra1, giocatoriSquadra2, falli1, falli2, teamNumber) => {
  const pieni1 = calculateTeamTotal(giocatoriSquadra1, 'pieni');
  const pieni2 = calculateTeamTotal(giocatoriSquadra2, 'pieni');
  
  // 1. Vince chi ha fatto più pieni
  if (pieni1 !== pieni2) {
    if (pieni1 > pieni2) {
      return teamNumber === 1 ? 'win' : 'loss';
    } else {
      return teamNumber === 1 ? 'loss' : 'win';
    }
  }
  
  // 2. Se i pieni sono pari, vince chi ha meno falli di squadra
  const falliteam1 = falli1 || 0;
  const falliteam2 = falli2 || 0;
  
  if (falliteam1 !== falliteam2) {
    if (falliteam1 < falliteam2) {
      return teamNumber === 1 ? 'win' : 'loss';
    } else {
      return teamNumber === 1 ? 'loss' : 'win';
    }
  }
  
  // 3. Se anche i falli sono pari, vince chi ha meno vuoti
  const vuoti1 = calculateTeamTotal(giocatoriSquadra1, 'vuoti');
  const vuoti2 = calculateTeamTotal(giocatoriSquadra2, 'vuoti');
  
  if (vuoti1 !== vuoti2) {
    if (vuoti1 < vuoti2) {
      return teamNumber === 1 ? 'win' : 'loss';
    } else {
      return teamNumber === 1 ? 'loss' : 'win';
    }
  }
  
  // Se tutti i criteri sono pari, è pareggio
  return 'draw';
};